import React, { useState, useRef, useEffect } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { ShipmentEntry, QuantityUnit, TemperatureUnit, StorageType } from '../../../types/shipment';
import { Supplier } from '../../../types/supplier';
import { updateIngredientReceivingEntry, deleteIngredientReceivingEntry, propagateStorageTypeChange } from '../../../store/slices/ingredientReceivingSlice';
import { SearchableIngredientDropdown } from '../../../components/SearchableIngredientDropdown';
import { ClickableIdentifier } from '../../../components/haccp/ClickableIdentifier';
import { useTraceability } from '../../../components/haccp/TraceabilityContext';
import { scrollToFieldAndHighlight } from '../../../utils/scrollUtils';

interface ShipmentEntryRowProps {
  entry: ShipmentEntry;
  suppliers: Supplier[];
  isNew?: boolean;
  onSave?: (entry: ShipmentEntry) => void;
  onCancel?: () => void;
  isHighlighted?: boolean;
  targetField?: string;
  isFlagged?: boolean;
}

export const ShipmentEntryRow: React.FC<ShipmentEntryRowProps> = ({
  entry,
  suppliers,
  isNew = false,
  onSave,
  onCancel,
  isHighlighted = false,
  targetField,
  isFlagged
}) => {
  const dispatch = useAppDispatch();
  const { openTraceability } = useTraceability();
  const [isEditMode, setIsEditMode] = useState(isNew);
  const [editedEntry, setEditedEntry] = useState<ShipmentEntry>(entry);

  // Highlight state for deep-linking
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [isHighlightActive, setIsHighlightActive] = useState(isHighlighted);

  useEffect(() => {
    if (isHighlighted) {
      rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setIsHighlightActive(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted]);

  // Field-level deep-linking: scroll to and highlight specific field
  useEffect(() => {
    if (!targetField) return;
    const timer = setTimeout(() => {
      scrollToFieldAndHighlight(`recv-${entry.id}-field-${targetField}`, {
        highlightColor: isFlagged ? 'red' : 'blue'
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [targetField, entry.id, isFlagged]);

  const quantityUnits: QuantityUnit[] = ['lbs', 'kg', 'oz', 'g', 'gallons', 'liters'];

  // Get ingredients for selected supplier
  const selectedSupplier = suppliers.find(s => s.id === editedEntry.supplierId);
  const availableIngredients = selectedSupplier?.ingredientsSupplied || [];

  // Handle supplier change
  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setEditedEntry({
      ...editedEntry,
      supplierId: supplierId,
      supplierName: supplier?.supplierName || '',
      ingredient: '' // Clear ingredient when supplier changes
    });
  };

  // Handle temperature unit toggle with conversion
  const handleTemperatureUnitToggle = () => {
    const newUnit: TemperatureUnit = editedEntry.temperatureUnit === 'F' ? 'C' : 'F';
    let convertedTemp = editedEntry.receivingTemperature;

    if (editedEntry.temperatureUnit === 'F' && newUnit === 'C') {
      // F to C: (F - 32) × 5/9
      convertedTemp = Math.round((((editedEntry.receivingTemperature - 32) * 5) / 9) * 10) / 10;
    } else if (editedEntry.temperatureUnit === 'C' && newUnit === 'F') {
      // C to F: (C × 9/5) + 32
      convertedTemp = Math.round(((editedEntry.receivingTemperature * 9) / 5 + 32) * 10) / 10;
    }

    setEditedEntry({
      ...editedEntry,
      receivingTemperature: convertedTemp,
      temperatureUnit: newUnit
    });
  };

  // Handle save (for existing entries)
  const handleSave = async () => {
    if (isNew && onSave) {
      // New entry - call parent's onSave
      onSave(editedEntry);
    } else {
      // Existing entry - use Redux thunk
      try {
        await dispatch(updateIngredientReceivingEntry(editedEntry)).unwrap();
        // If storage type changed, propagate to all entries with the same ingredient
        if (editedEntry.storageType !== entry.storageType && editedEntry.ingredient) {
          dispatch(propagateStorageTypeChange({
            ingredientName: editedEntry.ingredient,
            storageType: editedEntry.storageType,
            supplierId: editedEntry.supplierId
          }));
        }
        setIsEditMode(false);
      } catch (error) {
        console.error('Failed to update entry:', error);
        alert('Failed to update entry. Please try again.');
      }
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (isNew && onCancel) {
      onCancel();
    } else {
      setEditedEntry(entry);
      setIsEditMode(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (window.confirm('Delete this entry? This cannot be undone.')) {
      try {
        await dispatch(deleteIngredientReceivingEntry(entry.id)).unwrap();
      } catch (error) {
        console.error('Failed to delete entry:', error);
        alert('Failed to delete entry. Please try again.');
      }
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  };

  const cellStyle: React.CSSProperties = {
    padding: '8px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle'
  };

  const readOnlyStyle: React.CSSProperties = {
    ...cellStyle,
    color: '#333'
  };

  if (!isEditMode) {
    // View mode - display values
    return (
      <tr
        ref={rowRef}
        style={{
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: isHighlightActive ? 'rgba(33, 150, 243, 0.15)' : undefined,
          transition: 'background-color 0.3s ease-in-out',
          boxShadow: isHighlightActive ? 'inset 0 0 0 2px #2196F3' : undefined
        }}
      >
        <td id={`recv-${entry.id}-field-supplierName`} style={readOnlyStyle}>{entry.supplierName || '-'}</td>
        <td id={`recv-${entry.id}-field-ingredient`} style={readOnlyStyle}>{entry.ingredient || '-'}</td>
        <td id={`recv-${entry.id}-field-storageType`} style={readOnlyStyle}>
          {entry.storageType === 'frozen' ? 'Frozen' : entry.storageType === 'refrigerated' ? 'Refrigerated' : 'Non-Refrigerated'}
        </td>
        <td id={`recv-${entry.id}-field-lotNumber`} style={readOnlyStyle}>
          {entry.lotNumber ? (
            <ClickableIdentifier value={entry.lotNumber} type="lotNumber" onClick={openTraceability} />
          ) : '-'}
        </td>
        <td id={`recv-${entry.id}-field-quantity`} style={readOnlyStyle}>{entry.quantity > 0 ? `${entry.quantity} ${entry.unit}` : '-'}</td>
        <td id={`recv-${entry.id}-field-conditionUponArrival`} style={readOnlyStyle}>{entry.conditionUponArrival || '-'}</td>
        <td id={`recv-${entry.id}-field-receivingTemperature`} style={readOnlyStyle}>
          {entry.receivingTemperature !== 0 ? `${entry.receivingTemperature}°${entry.temperatureUnit}` : '-'}
        </td>
        <td id={`recv-${entry.id}-field-receivedBy`} style={readOnlyStyle}>{entry.receivedBy || '-'}</td>
        <td id={`recv-${entry.id}-field-correctiveAction`} style={readOnlyStyle}>{entry.correctiveAction || '-'}</td>
        <td style={{ ...cellStyle, textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => setIsEditMode(true)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1976D2')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2196F3')}
              title="Edit entry"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              style={{
                padding: '6px 10px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
              title="Delete entry"
            >
              ✕
            </button>
          </div>
        </td>
      </tr>
    );
  }

  // Edit mode - display inputs
  return (
    <tr style={{ backgroundColor: isNew ? '#f0f8ff' : 'white' }}>
      {/* Supplier Name */}
      <td style={cellStyle}>
        <select value={editedEntry.supplierId} onChange={e => handleSupplierChange(e.target.value)} style={inputStyle}>
          <option value="">Select Supplier</option>
          {suppliers.length === 0 && (
            <option value="" disabled>
              (No suppliers available)
            </option>
          )}
          {suppliers.map(supplier => (
            <option key={supplier.id} value={supplier.id}>
              {supplier.supplierName}
            </option>
          ))}
        </select>
      </td>

      {/* Ingredient */}
      <td style={cellStyle}>
        <SearchableIngredientDropdown
          value={editedEntry.ingredient}
          options={availableIngredients}
          onChange={(value) => setEditedEntry({ ...editedEntry, ingredient: value })}
          disabled={!editedEntry.supplierId}
          placeholder="Select Ingredient"
          style={inputStyle}
        />
      </td>

      {/* Storage Type */}
      <td style={cellStyle}>
        <select
          value={editedEntry.storageType}
          onChange={e => setEditedEntry({ ...editedEntry, storageType: e.target.value as StorageType })}
          style={inputStyle}
        >
          <option value="non_refrigerated">Non-Refrigerated</option>
          <option value="refrigerated">Refrigerated</option>
          <option value="frozen">Frozen</option>
        </select>
      </td>

      {/* Lot Number */}
      <td style={cellStyle}>
        <input
          type="text"
          value={editedEntry.lotNumber}
          onChange={e => setEditedEntry({ ...editedEntry, lotNumber: e.target.value })}
          style={inputStyle}
          placeholder="Lot #"
        />
      </td>

      {/* Quantity + Unit */}
      <td style={cellStyle}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <input
            type="number"
            value={editedEntry.quantity || ''}
            onChange={e => setEditedEntry({ ...editedEntry, quantity: parseFloat(e.target.value) || 0 })}
            style={{ ...inputStyle, width: '60px' }}
            placeholder="0"
            min="0"
            step="0.01"
          />
          <select
            value={editedEntry.unit}
            onChange={e => setEditedEntry({ ...editedEntry, unit: e.target.value as QuantityUnit })}
            style={{ ...inputStyle, width: '80px' }}
          >
            {quantityUnits.map(unit => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </div>
      </td>

      {/* Condition Upon Arrival */}
      <td style={cellStyle}>
        <input
          type="text"
          value={editedEntry.conditionUponArrival}
          onChange={e => setEditedEntry({ ...editedEntry, conditionUponArrival: e.target.value })}
          style={inputStyle}
          placeholder="Good"
        />
      </td>

      {/* Receiving Temperature + Unit Toggle */}
      <td style={cellStyle}>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <input
            type="number"
            value={editedEntry.receivingTemperature || ''}
            onChange={e => setEditedEntry({ ...editedEntry, receivingTemperature: parseFloat(e.target.value) || 0 })}
            style={{ ...inputStyle, width: '60px' }}
            placeholder="0"
            step="0.1"
          />
          <button
            type="button"
            onClick={handleTemperatureUnitToggle}
            style={{
              padding: '8px 12px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              minWidth: '45px'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1976D2')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2196F3')}
          >
            °{editedEntry.temperatureUnit}
          </button>
        </div>
      </td>

      {/* Received By */}
      <td style={cellStyle}>
        <input
          type="text"
          value={editedEntry.receivedBy}
          onChange={e => setEditedEntry({ ...editedEntry, receivedBy: e.target.value })}
          style={inputStyle}
          placeholder="Name"
        />
      </td>

      {/* Corrective Action */}
      <td style={cellStyle}>
        <input
          type="text"
          value={editedEntry.correctiveAction}
          onChange={e => setEditedEntry({ ...editedEntry, correctiveAction: e.target.value })}
          style={inputStyle}
          placeholder="None"
        />
      </td>

      {/* Actions (Save/Cancel) */}
      <td style={{ ...cellStyle, textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: '6px 10px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
            title="Save changes"
          >
            Save
          </button>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: '6px 10px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
            title="Cancel"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
};
